import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  IsLoadingInfo,
  load,
  SelectActualClientId,
} from "../redux/reducers/clientNewContact.reducer";
import {
  IsLoadingDropdownInfo,
  loadDropdownInfo,
  SelectDropdownsInfo,
  setIsLoadingDropdownInfo,
} from "../redux/reducers/clientDropdownsInfo.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import {
  ROUTE_CLIENTS,
  ROUTE_FORBIDDEN_PAGE,
} from "../../SharedModule/utils/constants";
import { useNavigate } from "react-router";
import {
  IsClientsLoaded,
  SelectActualClientForHeaderInfo,
  SelectClientsIds,
  setActualClientForHeaderInfo,
  setIsActualClientForHeaderInfoLoading,
} from "../redux/reducers/clientsList.reducer";
import { Client } from "../types/clients-module-types";

import { useClientsList } from "./useClientsList";

export const useClientNewContact = (clientId: number) => {
  useClientsList();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userRolesList, rolesLoaded } = useSelector(selectUserRoles);
  const isLoadingInfo = useSelector(IsLoadingInfo);
  const actualClientId = useSelector(SelectActualClientId);
  const dropdownsValues = useSelector(SelectDropdownsInfo);
  const isLoadingDropdownInfo = useSelector(IsLoadingDropdownInfo);

  // To check is header info is already loaded
  const ClientForHeaderInfo: Client = useSelector(
    SelectActualClientForHeaderInfo
  );
  // to check if clientId is in list of clients that user could see
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const clientsIds = useSelector(SelectClientsIds);

  const loadNewContactInfo = useCallback(async () => {
    if (rolesLoaded && isClientsLoaded) {
      // This check should go in all clients custom hooks
      if (!clientsIds.includes(clientId)) {
        dispatch(
          addToast({
            mode: "error",
            message: "You're not allowed to see this Client Page",
          })
        );
        navigate(ROUTE_FORBIDDEN_PAGE);
        return true;
      }
      dispatch(load(clientId));
      // Load info header when user put a new URL
      if (
        !ClientForHeaderInfo ||
        (ClientForHeaderInfo && ClientForHeaderInfo.id !== clientId)
      ) {
        dispatch(setIsActualClientForHeaderInfoLoading(true));
        let headerInfo = await ClientsService.getClientModuleHeaderInfo(
          clientId
        )
          .then((response) => {
            if (!response.success) {
              dispatch(
                addToast({
                  mode: "error",
                  message: response.message,
                })
              );
              dispatch(setIsActualClientForHeaderInfoLoading(false));
              navigate(ROUTE_CLIENTS);
              return true;
            } else {
              return response.data;
            }
          })
          .catch(() => {
            dispatch(
              addToast({
                mode: "error",
                message:
                  "Something wrong happened while getting the client header information.",
              })
            );
            dispatch(setIsActualClientForHeaderInfoLoading(false));
            navigate(ROUTE_CLIENTS);
            return true;
          });
        dispatch(setActualClientForHeaderInfo(headerInfo));
      }
      // page info
      if (!dropdownsValues) {
        dispatch(setIsLoadingDropdownInfo(true));
        const dropdownsInfo = await ClientsService.getClientsDropdownsInfo();
        dispatch(loadDropdownInfo(dropdownsInfo.data));
      }
    }
  }, [dispatch, rolesLoaded, isClientsLoaded, ClientForHeaderInfo, dropdownsValues]);

  useEffect(() => {
    loadNewContactInfo();
  }, [loadNewContactInfo]);

  return {
    isLoadingInfo,
    isLoadingDropdownInfo,
    actualClientId,
    userRolesList,
    rolesLoaded,
    dropdownsValues,
  };
};
